import React, { createContext, forwardRef, useImperativeHandle } from 'react';
import io from 'socket.io-client';
import { useMemoOnce } from '@hilma/tools';

import { SocketRooms, HilmaSocket, SocketProviderProps, GenericEvents } from './types';

const { JOIN, LEAVE, GET_ROOMS, LEAVE_ALL } = GenericEvents;

export const SocketContext = createContext<HilmaSocket | null>(null);

/**
 * Component that creates a socket instance and provides it via the React Context API
 * Make sure to wrap your react component tree 
 * @param uri The uri that we'll connect to, including the namespace, where '/' is the default one (e.g. http://localhost:4000/somenamespace) 
 * @param options Any connect options that we want to pass along 
 */
const SocketProvider = forwardRef(({ children, uri, options }: SocketProviderProps, ref?: React.Ref<HilmaSocket>) => {

	const socket = useMemoOnce(() => {
		const socket = uri ? io(uri, options) as HilmaSocket : io(options) as HilmaSocket;

		socket.join = (name, fn = () => { }) => new Promise((resolve, reject) => {
			socket.emit(JOIN, name, (err: any) => {
				const parsedError = JSON.parse(err);
				fn(parsedError);
				if (parsedError) { reject(parsedError); return; }
				resolve();
			});
		});

		socket.leave = (name, fn = () => { }) => new Promise((resolve, reject) => {
			socket.emit(LEAVE, name, (err: any) => {
				const parsedError = JSON.parse(err);
				fn(parsedError);
				if (parsedError) { reject(parsedError); return; }
				resolve();
			});
		});

		socket.getRooms = (fn = () => { }) => new Promise(resolve => {
			socket.emit(GET_ROOMS, (rooms: string) => {
				const parsedRooms = JSON.parse(rooms) as SocketRooms;
				fn(parsedRooms);
				resolve(parsedRooms);
			});
		});

		socket.leaveAll = () => { socket.emit(LEAVE_ALL); };

		return socket;
	});

	useImperativeHandle(ref, () => socket, [socket]);

	return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
});

export default SocketProvider;